import { defineComponent as _defineComponent } from 'vue'

import { Dropdown, DropdownMenu, DropdownItem } from 'element-ui'

const __default__ = {
  name: 'UserRoles',
  components: {
    [Dropdown.name]: Dropdown,
    [DropdownMenu.name]: DropdownMenu,
    [DropdownItem.name]: DropdownItem,
  },
}

import { ref, reactive, computed, onMounted, getCurrentInstance } from 'vue'
import axios from 'axios'
import { Auth } from 'aws-amplify'
import { API_LOCATION } from '@/api-config'
import { formatGroupDate } from '@/plugins/dates'
import { pageOffset, syncPagingQuery, readPagingQuery, downloadCsv, fetchAllPages } from '@/util/listPage'
import TabRail from '@/components/TabRail.vue'
import type { DataTableColumn } from '@/components/DataTable.vue'

type RoleRow = { id: number; name: string; description: string | null; time_created: string | null; default_role: number; rowKey: string }

// Default and custom roles come from two tables and can share an id.

export default /*#__PURE__*/_defineComponent({
  ...__default__,
  setup(__props) {

// Roles list, server paged: GET /companies/{id}/roles?count&offset&q&sort&dir
// (get-company_roles paged mode) returns { roles, total_rows } with default and
// custom roles in one set; `default_role` is 1 for the built-in ones.
function withKey(r: any): RoleRow {
  return { ...r, rowKey: (r.default_role ? 'd' : 'c') + r.id }
}

const { proxy } = getCurrentInstance() as any
const companyId = localStorage.getItem('companyId')
const baseUrl = `${API_LOCATION}companies/${companyId}`

const permissions: string[] = JSON.parse(localStorage.getItem('permissions') || '[]')
function can(scope: string) {
  return permissions.includes(scope)
}

const loading = ref(false)
// Starts true so the table shows its skeleton until the first fetch lands.
const refreshing = ref(true)
const roles = ref<RoleRow[]>([])
const totalRows = ref(0)
const perPageOptions = [10, 25, 50, 100]
const perPage = ref(10)
const currentPage = ref(1)
let didInitialFetch = false

function onTab(index: number) {
  if (index === 0) proxy.$router.push('/settings/user-management')
}

const sortOptions = [
  { key: 'type', label: 'Default roles first' },
  { key: 'name', label: 'Name' },
  { key: 'created', label: 'Created' },
]
const sort = ref<{ key: string; dir: 'asc' | 'desc' }>({ key: 'type', dir: 'desc' })

const columns: DataTableColumn[] = [
  { key: 'name', label: 'Name', sortable: true },
  { key: 'description', label: 'Description' },
  { key: 'time_created', label: 'Created', sortable: true, sortKey: 'created', width: '160px' },
]

function onSortChange(value: { key: string; dir: 'asc' | 'desc' }) {
  sort.value = value
  currentPage.value = 1
  if (didInitialFetch) fetchRoles()
}

const filterValues = ref<Record<string, any>>({ q: '' })
const filterSchema = computed(() => [
  { key: 'q', type: 'text', label: 'Name or description', placeholder: 'Type to search', operatorLabel: 'contains', primary: true },
])

function onFiltersChanged(values: Record<string, any>) {
  filterValues.value = { ...filterValues.value, ...values }
  if (!didInitialFetch) {
    didInitialFetch = true
    fetchRoles()
    return
  }
  currentPage.value = 1
  fetchRoles()
}

function onPerPageChange(value: number) {
  perPage.value = value
  currentPage.value = 1
  fetchRoles()
}

function onPageChange(page: number) {
  currentPage.value = page
  fetchRoles()
}

function listQuery(count: number, offset: number) {
  const f = filterValues.value
  const params = new URLSearchParams()
  params.set('count', String(count))
  params.set('offset', String(offset))
  if (f.q) params.set('q', String(f.q).trim())
  params.set('sort', sort.value.key)
  params.set('dir', sort.value.dir)
  return `${baseUrl}/roles?${params.toString()}`
}

async function authConfig() {
  const session = await Auth.currentSession()
  return { headers: { Authorization: (session as any).idToken.jwtToken } }
}

const dialog = reactive({
  show: false, variant: 'warning', title: '', message: '', showCancel: false,
  confirmText: 'Ok', confirmVariant: 'primary', onConfirm: null as null | (() => void), redirect: '',
})
function showDialog(opts: Partial<typeof dialog>) {
  Object.assign(dialog, { showCancel: false, confirmText: 'Ok', confirmVariant: 'primary', onConfirm: null, redirect: '' }, opts, { show: true })
}
function onDialogConfirm() {
  dialog.show = false
  const action = dialog.onConfirm
  const redirect = dialog.redirect
  dialog.onConfirm = null
  if (action) action()
  else if (redirect) proxy.$router.push(redirect)
}

function handleAuthError(err: any): boolean {
  if (err?.response?.status === 401) {
    proxy.$router.push('/forbidden')
    return true
  }
  return false
}

async function fetchRoles() {
  refreshing.value = true
  try {
    const config = await authConfig()
    const res = await axios.get(listQuery(perPage.value, pageOffset(currentPage.value, perPage.value)), config)
    roles.value = (Array.isArray(res.data.roles) ? res.data.roles : []).map(withKey)
    totalRows.value = Number(res.data.total_rows) || 0
    syncPagingQuery(proxy.$router, proxy.$route, currentPage.value, perPage.value)
  } catch (err: any) {
    if (!handleAuthError(err)) {
      showDialog({ variant: 'danger', title: 'Could not load roles', message: 'Something went wrong. Please refresh and try again.' })
    }
  } finally {
    loading.value = false
    refreshing.value = false
  }
}

async function downloadRoles() {
  try {
    const config = await authConfig()
    const all = await fetchAllPages(async (count, offset) => {
      const res = await axios.get(listQuery(count, offset), config)
      return { rows: Array.isArray(res.data.roles) ? res.data.roles : [], total: Number(res.data.total_rows) || 0 }
    })
    downloadCsv('roles.csv', [
      { label: 'Name', value: (r: RoleRow) => r.name },
      { label: 'Description', value: (r: RoleRow) => r.description || '' },
      { label: 'Type', value: (r: RoleRow) => (r.default_role ? 'Default' : 'Custom') },
      { label: 'Created', value: (r: RoleRow) => (r.time_created ? formatGroupDate(r.time_created, { dayOfWeek: false }) : '') },
    ], all)
  } catch (err) {
    proxy.$notify({ type: 'danger', message: 'Failed to export roles', timeout: 3000 })
  }
}

function editRole(r: RoleRow) {
  proxy.$router.push({ path: '/settings/roles/edit', query: { id: String(r.id) } })
}

async function createRole() {
  loading.value = true
  try {
    const config = await authConfig()
    const res = await axios.post(`${baseUrl}/roles`, { role: { name: 'New role', description: '' } }, config)
    proxy.$router.push({ path: '/settings/roles/edit', query: { id: String(res.data) } })
  } catch (err: any) {
    if (!handleAuthError(err)) {
      showDialog({ variant: 'danger', title: 'Role not created', message: 'Something went wrong. Please try again.' })
    }
  } finally {
    loading.value = false
  }
}

function confirmDelete(r: RoleRow) {
  showDialog({
    variant: 'danger',
    title: `Delete ${r.name}?`,
    message: 'The role is permanently deleted. Anyone assigned to it loses the access it granted until they are given another role.',
    showCancel: true,
    confirmText: 'Delete role',
    confirmVariant: 'danger',
    onConfirm: () => deleteRole(r),
  })
}

async function deleteRole(r: RoleRow) {
  loading.value = true
  try {
    const config = await authConfig()
    await axios.delete(`${baseUrl}/roles/${r.id}`, config)
    proxy.$notify({ type: 'success', message: `${r.name} deleted`, icon: 'tim-icons icon-check-2' })
    await fetchRoles()
  } catch (err: any) {
    if (!handleAuthError(err)) {
      showDialog({ variant: 'danger', title: 'Role not deleted', message: 'Something went wrong. Please try again.' })
    }
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  const paging = readPagingQuery(proxy.$route, 10)
  perPage.value = perPageOptions.includes(paging.perPage) ? paging.perPage : 10
  currentPage.value = paging.page
  setTimeout(() => { if (!didInitialFetch) { didInitialFetch = true; fetchRoles() } }, 800)
})

return { __sfc: true,withKey, proxy, companyId, baseUrl, permissions, can, loading, refreshing, roles, totalRows, perPageOptions, perPage, currentPage, didInitialFetch, onTab, sortOptions, sort, columns, onSortChange, filterValues, filterSchema, onFiltersChanged, onPerPageChange, onPageChange, listQuery, authConfig, dialog, showDialog, onDialogConfirm, handleAuthError, fetchRoles, downloadRoles, editRole, createRole, confirmDelete, deleteRole, formatGroupDate, TabRail }
}

})